import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Search, RefreshCw, Sparkles, Loader2, Database, ExternalLink, ToggleLeft, ToggleRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "ha-filler", "botox", "skin-booster", "body-filler",
  "lipolysis", "exosome", "iv-therapy", "lidocaine-cream", "etc",
];

const TOPIC_LABELS = {
  product: "Product",
  comparison: "Comparison",
  buying_guide: "Buying guide",
  authenticity: "Authenticity",
  regulatory: "Regulatory",
  news: "News",
  clinical: "Clinical",
  price_guide: "Pricing",
};

export const KeywordsAdmin = () => {
  const [keywords, setKeywords] = useState([]);
  const [counts, setCounts] = useState({ total: 0, in_catalog: 0, external: 0, active: 0 });
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [filter, setFilter] = useState({ in_catalog: "all", category: "all", q: "" });
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.in_catalog !== "all") params.in_catalog = filter.in_catalog === "owned";
      if (filter.category !== "all") params.category = filter.category;
      if (filter.q) params.q = filter.q;
      const { data } = await axios.get(`${API}/admin/journal/keywords`, { params });
      setKeywords(data.keywords || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error("Failed to load keywords");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.in_catalog, filter.category, filter.q]);

  const seed = async () => {
    if (!window.confirm("Seed/Refresh keyword repository?\n\nThis will add owned brands from your catalog + ~65 famous external anchor brands. Existing entries are kept (no overwrites).")) return;
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API}/admin/journal/keywords/seed`);
      toast.success(`Seeded ${data.created_in_catalog} owned + ${data.created_external} external keywords`, {
        description: `Total in DB: ${data.total_in_db} · Skipped: ${data.skipped}`,
      });
      await load();
    } catch (e) {
      toast.error("Seed failed");
    } finally {
      setSeeding(false);
    }
  };

  const toggleActive = async (kw) => {
    try {
      const { data } = await axios.put(`${API}/admin/journal/keywords/${kw.id}`, { active: !kw.active });
      setKeywords((arr) => arr.map((k) => k.id === kw.id ? data : k));
      toast.success(`${kw.brand} ${data.active ? "activated" : "paused"}`);
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const updatePriority = async (kw, priority) => {
    try {
      const { data } = await axios.put(`${API}/admin/journal/keywords/${kw.id}`, { priority });
      setKeywords((arr) => arr.map((k) => k.id === kw.id ? data : k));
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const removeKw = async (kw) => {
    if (!window.confirm(`Delete keyword "${kw.brand}" permanently?`)) return;
    try {
      await axios.delete(`${API}/admin/journal/keywords/${kw.id}`);
      setKeywords((arr) => arr.filter((k) => k.id !== kw.id));
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const filtered = useMemo(() => keywords, [keywords]);

  return (
    <section data-testid="admin-keywords">
      {/* Header + Counts */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
        <div className="lg:col-span-7 border border-neutral-200 p-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 font-medium mb-2">Keyword repository</div>
          <h2 className="font-heading text-3xl font-black tracking-tight mb-1">Post generation pool.</h2>
          <p className="text-sm text-neutral-600 font-light max-w-2xl">
            Brands the scheduler draws from when publishing articles. <span className="font-bold text-black">60%</span> of posts target owned brands · <span className="font-bold text-black">40%</span> target famous external brands as SEO anchors with auto-interlinks back to your catalog.
          </p>
          <div className="mt-5 grid grid-cols-4 gap-px bg-neutral-200 border border-neutral-200" data-testid="keywords-counts">
            <Counter label="Total" value={counts.total} />
            <Counter label="In catalog" value={counts.in_catalog} />
            <Counter label="External" value={counts.external} />
            <Counter label="Active" value={counts.active} />
          </div>
        </div>
        <div className="lg:col-span-5 border border-neutral-200 p-6 flex flex-col gap-3">
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 font-medium">Bulk actions</div>
          <button
            onClick={seed}
            disabled={seeding}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-black text-white hover:bg-neutral-800 text-sm font-medium disabled:opacity-50"
            data-testid="keywords-seed-btn"
          >
            {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Seed / refresh defaults
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-black hover:bg-black hover:text-white text-sm font-medium"
            data-testid="keywords-add-btn"
          >
            <Plus className="w-4 h-4" /> Add keyword
          </button>
          <button
            onClick={load}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 text-xs text-neutral-600 hover:text-black"
            data-testid="keywords-refresh-btn"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="keywords-filters">
        <div className="flex gap-1 border border-neutral-300">
          {[
            { id: "all", label: "All" },
            { id: "owned", label: "In catalog" },
            { id: "external", label: "External" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setFilter((f) => ({ ...f, in_catalog: t.id }))}
              className={`px-3 py-1.5 text-xs font-bold tracking-wide ${filter.in_catalog === t.id ? "bg-black text-white" : "hover:bg-neutral-100"}`}
              data-testid={`filter-pool-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <select
          value={filter.category}
          onChange={(e) => setFilter((f) => ({ ...f, category: e.target.value }))}
          className="px-3 py-1.5 text-xs border border-neutral-300 bg-white"
          data-testid="filter-category"
        >
          <option value="all">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="flex items-center gap-1 border border-neutral-300 px-2">
          <Search className="w-3.5 h-3.5 text-neutral-400" />
          <input
            type="text"
            placeholder="Search brand…"
            value={filter.q}
            onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
            className="px-2 py-1.5 text-xs outline-none w-40"
            data-testid="filter-search"
          />
        </div>
        <div className="text-xs text-neutral-500 ml-auto">{filtered.length} shown</div>
      </div>

      {/* Add form */}
      {showAdd && <AddKeywordForm onDone={() => { setShowAdd(false); load(); }} onCancel={() => setShowAdd(false)} />}

      {/* Table */}
      {loading ? (
        <div className="py-16 flex items-center justify-center text-neutral-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading keywords…
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 border border-dashed border-neutral-300 text-center text-sm text-neutral-500" data-testid="keywords-empty">
          No keywords match. Click <span className="font-bold">Seed</span> to populate defaults.
        </div>
      ) : (
        <div className="border border-neutral-200 overflow-x-auto" data-testid="keywords-table">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-medium">
                <th className="px-3 py-3">Brand</th>
                <th className="px-3 py-3">Category</th>
                <th className="px-3 py-3">Pool</th>
                <th className="px-3 py-3">Priority</th>
                <th className="px-3 py-3">Interlinks</th>
                <th className="px-3 py-3">Topics</th>
                <th className="px-3 py-3">Posts</th>
                <th className="px-3 py-3">Active</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((kw) => (
                <tr key={kw.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`kw-row-${kw.id}`}>
                  <td className="px-3 py-3 font-medium">
                    {kw.brand}
                    {kw.manufacturer && <div className="text-[10px] text-neutral-400 font-light">{kw.manufacturer}</div>}
                  </td>
                  <td className="px-3 py-3 text-xs"><code className="bg-neutral-100 px-1.5 py-0.5">{kw.category}</code></td>
                  <td className="px-3 py-3 text-xs">
                    {kw.in_catalog ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700"><Database className="w-3 h-3" /> Owned</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-700"><ExternalLink className="w-3 h-3" /> Anchor</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <select
                      value={kw.priority}
                      onChange={(e) => updatePriority(kw, Number(e.target.value))}
                      className="text-xs border border-neutral-300 px-1.5 py-0.5"
                      data-testid={`kw-priority-${kw.id}`}
                    >
                      {[1,2,3,4,5].map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-3 text-xs text-neutral-600">{kw.interlink_product_ids?.length || 0}</td>
                  <td className="px-3 py-3 text-[10px] text-neutral-500 max-w-[180px] truncate" title={kw.target_topics?.join(", ")}>
                    {(kw.target_topics || []).slice(0, 3).map((t) => TOPIC_LABELS[t] || t).join(", ")}
                    {kw.target_topics?.length > 3 && ` +${kw.target_topics.length - 3}`}
                  </td>
                  <td className="px-3 py-3 text-xs text-neutral-700 font-bold tabular-nums">{kw.posts_count || 0}</td>
                  <td className="px-3 py-3">
                    <button onClick={() => toggleActive(kw)} data-testid={`kw-toggle-${kw.id}`}>
                      {kw.active ? <ToggleRight className="w-5 h-5 text-emerald-600" /> : <ToggleLeft className="w-5 h-5 text-neutral-400" />}
                    </button>
                  </td>
                  <td className="px-3 py-3">
                    <button
                      onClick={() => removeKw(kw)}
                      className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                      data-testid={`kw-delete-${kw.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

const Counter = ({ label, value }) => (
  <div className="bg-white p-4">
    <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-medium">{label}</div>
    <div className="font-heading text-2xl font-black tracking-tight mt-1 tabular-nums">{value}</div>
  </div>
);

const AddKeywordForm = ({ onDone, onCancel }) => {
  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState("ha-filler");
  const [inCatalog, setInCatalog] = useState(false);
  const [manufacturer, setManufacturer] = useState("");
  const [priority, setPriority] = useState(3);
  const [topics, setTopics] = useState(["comparison", "buying_guide"]);
  const [saving, setSaving] = useState(false);

  const allTopics = Object.keys(TOPIC_LABELS);
  const toggleTopic = (t) => setTopics((arr) => arr.includes(t) ? arr.filter((x) => x !== t) : [...arr, t]);

  const submit = async (e) => {
    e.preventDefault();
    if (!brand.trim()) { toast.error("Brand name is required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/journal/keywords`, {
        brand: brand.trim(),
        category,
        in_catalog: inCatalog,
        manufacturer: manufacturer.trim(),
        priority,
        target_topics: topics,
        interlink_product_ids: [],
        active: true,
      });
      toast.success(`Added "${brand.trim()}"`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-neutral-300 bg-neutral-50 p-5 mb-6" data-testid="kw-add-form">
      <h3 className="font-heading text-lg font-black tracking-tight mb-4">New keyword</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <label className="text-xs">
          <span className="block text-neutral-600 mb-1">Brand *</span>
          <input value={brand} onChange={(e) => setBrand(e.target.value)} required className="w-full px-2 py-1.5 border border-neutral-300 bg-white" data-testid="kw-form-brand" />
        </label>
        <label className="text-xs">
          <span className="block text-neutral-600 mb-1">Category</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-2 py-1.5 border border-neutral-300 bg-white" data-testid="kw-form-category">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="text-xs">
          <span className="block text-neutral-600 mb-1">Manufacturer</span>
          <input value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} className="w-full px-2 py-1.5 border border-neutral-300 bg-white" />
        </label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <label className="text-xs">
          <span className="block text-neutral-600 mb-1">Pool</span>
          <select value={inCatalog ? "owned" : "external"} onChange={(e) => setInCatalog(e.target.value === "owned")} className="w-full px-2 py-1.5 border border-neutral-300 bg-white" data-testid="kw-form-pool">
            <option value="external">External anchor</option>
            <option value="owned">In catalog</option>
          </select>
        </label>
        <label className="text-xs">
          <span className="block text-neutral-600 mb-1">Priority (1-5)</span>
          <input type="number" min="1" max="5" value={priority} onChange={(e) => setPriority(Number(e.target.value))} className="w-full px-2 py-1.5 border border-neutral-300 bg-white" />
        </label>
      </div>
      <div className="mb-4">
        <span className="block text-xs text-neutral-600 mb-1">Target topics</span>
        <div className="flex flex-wrap gap-1.5">
          {allTopics.map((t) => (
            <button
              key={t} type="button"
              onClick={() => toggleTopic(t)}
              className={`px-2 py-1 text-[11px] border ${topics.includes(t) ? "border-black bg-black text-white" : "border-neutral-300 hover:border-black"}`}
            >
              {TOPIC_LABELS[t]}
            </button>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className="px-4 py-2 bg-black text-white text-xs font-bold disabled:opacity-50" data-testid="kw-form-submit">
          {saving ? "Saving…" : "Add keyword"}
        </button>
        <button type="button" onClick={onCancel} className="px-4 py-2 border border-neutral-300 text-xs font-bold hover:border-black">Cancel</button>
      </div>
    </form>
  );
};
